import { useCallback, useEffect, useRef } from 'react';
import { AssetCard, type AssetListItem } from './AssetCard';
import { useSelection } from '../lib/selection-context';
import { useScrollContainerRef } from '../lib/layout-context';
import { GRID_COLS_DEFAULT, type GridCols } from '../lib/parse-grid-cols';

type Props = {
  assets: AssetListItem[];
  /**
   * `'library'` (default): Click navigiert zu /assets/:id, CardActions
   * zeigt Download/Open. `'trash'` (Sprint Papierkorb): Click = direkt
   * selection-toggle, keine CardActions (Routes returnen 404 für deleted).
   */
  mode?: 'library' | 'trash';
  /** Spalten auf lg+ (User-Regler 3–5). Mobile/Tablet bleiben fix bei 2/3. */
  cols?: GridCols;
  /** Stapel-Modus: Klick auf ein Stapel-Tile (memberCount>1) → Drill-in. */
  onStackOpen?: (stackKey: string) => void;
  /** Infinite-Scroll (nur Library). Trash lässt sie weg → kein Nachladen. */
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  /**
   * Roher `?q=`-String fürs Treffer-Highlighting. String-Primitive → wird an
   * `memo(AssetCard)` durchgereicht ohne Identitäts-Wechsel (kein memo-Break,
   * Session 23, `patterns/react.md`).
   */
  highlightQuery?: string;
  /**
   * Quick-Look (Session 30). `onHover`/`onQuickLook` MÜSSEN stabile Callbacks
   * sein (`useCallback([])`) → an `memo(AssetCard)` durchgereicht ohne memo-Break.
   * `onLeave` (Container-`mouseleave`) leert die grid-level Hover-Ref (Session-30
   * Spec-Eval EDGE-CASE: sonst „Space ohne Hover" unerreichbar).
   */
  onHover?: (id: string) => void;
  onLeave?: () => void;
  onQuickLook?: (id: string) => void;
  /** ⭐-Favoriten-Affordanz auf den Karten (Roadmap 3a Phase 1) — nur Library. */
  enableFavorite?: boolean;
};

// Literal-Klassen je lg-Spaltenzahl (kein dynamisches `lg:grid-cols-${n}` — das
// würde Tailwind nicht in den Build aufnehmen). Mobile 2 / sm 3 fix, lg+ = Wahl.
const COLS_LG_CLASS: Record<GridCols, string> = {
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
  5: 'lg:grid-cols-5',
};

// Reserve-Höhe für off-screen Cards (content-visibility-Platzhalter). `auto`
// merkt sich die echte Höhe nach dem ersten Render; 280px ist nur der Fallback,
// bis eine Card einmal sichtbar war (verhindert Scrollbar-Sprünge).
const INTRINSIC_SIZE = 'auto 280px';

/**
 * Library-Grid. KEINE JS-Virtualisierung mehr (die mountete/unmountete Cards bei
 * jedem Scroll-Schritt → React-Mount-Kosten in der Scroll-Geste → Ruckeln,
 * besonders auf langsameren Maschinen, Session 22). Stattdessen:
 *  - Alle GELADENEN Cards sind im DOM (mounten EINMAL beim Page-Load).
 *  - `content-visibility: auto` lässt den Browser off-screen Cards überspringen
 *    (Layout/Paint), ohne sie zu unmounten → Scrollen kostet KEINE React-Arbeit.
 *  - IntersectionObserver-Sentinel am Ende lädt die nächste Seite nach
 *    (rootMargin 600px → Prefetch vor dem Ende; synchroner In-Flight-Guard im
 *    Hook verhindert Doppel-Fetch).
 * Trade-off: das DOM wächst mit der geladenen Menge (statt gebounded). Für die
 * reale Nutzung (gefilterte/moderate Mengen) unkritisch; content-visibility hält
 * off-screen Cards billig. Reine Scroll-Glätte > gebounded DOM für diesen Fall.
 */
export function AssetGrid({
  assets,
  mode = 'library',
  cols = GRID_COLS_DEFAULT,
  onStackOpen,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  highlightQuery,
  onHover,
  onLeave,
  onQuickLook,
  enableFavorite = false,
}: Props) {
  const { count } = useSelection();
  const scrollRef = useScrollContainerRef();
  // Stabiler Callback statt `allIds`-Array-Prop: liefert die geladenen IDs lazy
  // zur Klick-Zeit (Shift-Range). Ein Array-Prop wechselte bei jedem Append die
  // Identität → memo(AssetCard) brach für ALLE Cards (Re-Render-Storm, Sess. 23,
  // im Trace als reiner Script/Reconciliation-Block sichtbar). `assetsRef` hält
  // die aktuelle Liste; `getAllIds` hat dank leerer Deps konstante Identität.
  const assetsRef = useRef(assets);
  assetsRef.current = assets;
  const getAllIds = useCallback(() => assetsRef.current.map((a) => a.id), []);
  const isTrash = mode === 'trash';
  const listboxActive = isTrash || count > 0;

  // Infinite-Scroll: Sentinel kurz vor dem Ende (rootMargin) triggert loadMore.
  // loadingMore/onLoadMore in Refs → der Observer liest sie aktuell, OHNE bei
  // jedem Append neu zu subscriben. Frühere Deps (`loadingMore`, `assets.length`)
  // bauten den Observer pro geladener Seite neu auf → das frische `observe()`
  // feuerte sofort wieder, solange der Sentinel im 600px-Margin lag → Burst-
  // Autoload von ~11 Seiten beim bloßen Öffnen (Session 23, „ruckelt sofort ab
  // erstem Screen"). Stabiler Observer (Deps nur [hasMore, scrollRef]) feuert nur
  // bei echten Intersection-Transitions; der synchrone In-Flight-Guard im Hook
  // (C2) bleibt die zweite Verteidigung gegen Doppel-Fetch.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const loadingMoreRef = useRef(loadingMore);
  loadingMoreRef.current = loadingMore;
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;
  useEffect(() => {
    if (!hasMore) return;
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !loadingMoreRef.current) onLoadMoreRef.current?.();
      },
      { root: scrollRef.current ?? null, rootMargin: '600px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, scrollRef]);

  return (
    <div
      role={listboxActive ? 'listbox' : undefined}
      aria-multiselectable={listboxActive ? true : undefined}
      aria-label={listboxActive ? 'Asset-Auswahl' : undefined}
      // Quick-Look: Verlassen des Grids leert die Hover-Ref (Space → no-op).
      onMouseLeave={onLeave}
    >
      <div className={`grid grid-cols-2 sm:grid-cols-3 ${COLS_LG_CLASS[cols]} gap-4`}>
        {assets.map((a) => (
          // content-visibility-Wrapper: off-screen überspringt der Browser
          // Layout/Paint dieser Card. `contain-intrinsic-size` reserviert die
          // Höhe, damit die Scrollbar stabil bleibt.
          <div
            key={a.id}
            style={{ contentVisibility: 'auto', containIntrinsicSize: INTRINSIC_SIZE }}
          >
            <AssetCard asset={a} getAllIds={getAllIds} mode={mode} onStackOpen={onStackOpen} highlightQuery={highlightQuery} onHover={onHover} onQuickLook={onQuickLook} enableFavorite={enableFavorite} />
          </div>
        ))}
      </div>
      {hasMore && <div ref={sentinelRef} aria-hidden style={{ height: 1 }} />}
    </div>
  );
}
